import React, { useEffect, useMemo, useRef, useState, useCallback, useLayoutEffect } from 'react'

import { formatEnumLabel } from 'utils'

import { Stack, Typography, useTheme, alpha } from '@mui/material'

import { hideScrollbar, useScrollFade } from '@repo/ui'

import { Search } from 'components'
import { FilterField } from './FilterField'

interface MultiSelectFilterProps {
  onChange?: () => void
  column?: object
  rows?: [],
  filter?: []
}

export const MultiSelectFilter = (props: MultiSelectFilterProps) => {
  const {
    column,
    rows = [],
    onChange = () => {},
    filter = [],
  } = props

  const theme = useTheme()
  // get unique value options from row
  const [selectedValues, setSelectedValues] = useState(filter)
  const [search, setSearch] = useState('')

  // A column carrying a `statuses` config (the same `{ key: { label, color,
  // borderColor?, backgroundColor? } }` shape StatusChip/StatusChipCell read)
  // renders its options as StatusChip-style toggle pills below — highlighted in
  // the status's own color when selected, grey/dim when not — instead of the
  // generic brand-tint selectable box. Options then come from the full status
  // catalog (every possible status), not just the values present on the
  // currently-loaded rows, so a status with no current rows is still filterable.
  // Status catalogs are small and curated (no sort/search needed) — the
  // search+scroll treatment below is for large row-derived facets only.
  const statuses = (column as any)?.statuses

  /**
   * The facet values a row contributes — through the column's own resolver when it has one.
   *
   * ⚠️ THIS USED TO READ `row[column.field]` ONLY, so a DERIVED column contributed nothing:
   * a value computed rather than stored (inside a jsonb blob, off a relation) has no
   * `row[field]`, and the facet list came out as a single `undefined` — a checkbox with a
   * blank label that, when ticked, emitted `isAnyOf([undefined])` and emptied the table. A
   * control that is visibly present, accepts a click, and can only ever remove every row.
   *
   * Two hooks, in order, both optional so every existing call site is untouched:
   *
   *   `filterValueGetter` — for a column whose FACETS are not its displayed value. A list
   *                         column renders "handbags, leather-goods" in one cell but wants
   *                         `handbags` and `leather-goods` as two separate options, and
   *                         `.flat()` below is what turns the returned array into them.
   *   `valueGetter`       — the grid's own, when display and facet are the same string.
   *
   * Found on `/internal/signups` (VRG-545), where every website column is derived.
   */
  const facetValuesOf = (row: any) => {
    const raw = row?.[column.field]
    const getter = (column as any)?.filterValueGetter ?? (column as any)?.valueGetter
    if (typeof getter !== 'function') return raw
    try {
      return getter(raw, row, column, undefined)
    } catch {
      // A getter that throws on a partial row must not take the whole facet down with it.
      return raw
    }
  }

  const options = useMemo(() => {
    if (statuses) return Object.keys(statuses)
    // catch for if columns is being dynamically generated; sort alphabetically (VRG-141).
    const raw =
      column.options != undefined
        ? column.options
        : // `!= null` drops the blank option described above. It is `!=`, not `!==`, so it
          // catches `undefined` too — a row missing the field entirely is the common case,
          // not the edge one.
          Array.from(new Set(rows.map(facetValuesOf).flat())).filter(v => v != null && v !== '')
    // Respect an explicitly-provided option order (e.g. Size, small→big); otherwise
    // sort alphabetically (the default for row-derived facets like Best for).
    return column.sortOptions === false ? [...raw] : [...raw].sort((a, b) => String(a).localeCompare(String(b)))
  }, [statuses, column.options, column.sortOptions, rows, column.field])
  // Long facets (e.g. Best for, ~50 categories) get a search box + fixed-height scroll;
  // short ones (Capability, or a status catalog) render as-is.
  const LONG = !statuses && options.length > 12

  /**
   * How an option READS, for searching.
   *
   * Search used to be `String(o).includes(...)` against the raw option VALUE, which is
   * correct only while the value is also the human-readable label — true for the
   * row-derived facets this was written for (Best for, Size), and false the moment a
   * facet's options are IDs. The analytics drawer's Owner / Product / Certificate facets
   * are uuid-valued, so typing "Aisha" matched nothing at all: a search box that is
   * visibly present, accepts input, and can never find anything.
   *
   * `getOptionLabel` is the column's own value→label resolver. It cannot be inferred
   * from `renderCell` (that returns a ReactNode, not text) and label-as-value is not a
   * safe substitute — the seed data alone has two distinct owners both named "John Doe",
   * so collapsing to labels would silently merge two people into one filter option.
   *
   * Optional, and defaulted to the old behaviour, so every existing call site is
   * unaffected.
   */
  const optionLabel = (o: unknown): string => {
    const resolve = (column as any)?.getOptionLabel
    return typeof resolve === 'function' ? String(resolve(o) ?? '') : String(o)
  }

  const shown = LONG && search.trim()
    ? options.filter(o => optionLabel(o).toLowerCase().includes(search.trim().toLowerCase()))
    : options
  useEffect(() => {
    setSelectedValues(filter);
  }, [filter])

  // Bottom fade on the scroll area (long facets) — a mask that fades the last rows out
  // while there's more below, and disappears once scrolled to the bottom (like the shop
  // grid). scrollable = content overflows; atBottom = scrolled to the end.
  // Shared hook (@repo/ui useScrollFade). It keeps the ResizeObserver this used to
  // own — the drawer mounts the panel before it has a height, so a plain effect
  // measures 0 — and adds a MutationObserver, so narrowing the list by search no
  // longer needs to be threaded through a dependency array here.
  const { ref: scrollRef, showFade, fadeSx } = useScrollFade({ fadeHeight: 40, enabled: LONG })

  const handleSelect = (option) => {
    let newSelectedValues = [...selectedValues]
    if (newSelectedValues.includes(option)) {
      newSelectedValues = newSelectedValues.filter((i) => i !== option);
    } 
    else {
      newSelectedValues = [...selectedValues, option]
    }
    onChange({'field': column?.field, 'operator': 'isAnyOf', 'value': newSelectedValues})
  }

  const handleClear = () => {
    onChange({'field': column?.field, 'operator': 'isAnyOf', 'value': []})
  }
  
  return (
    <FilterField label={column?.headerName} count={(selectedValues as any[]).length} showClear={selectedValues.length != 0} onClear={handleClear}>
      {LONG && (
        <Search
          value={search}
          onChange={setSearch}
          onClear={() => setSearch('')}
          placeholder={`Search ${String(column?.headerName ?? '').toLowerCase()}`}
          fullWidth
          showIcon={false}
          debounce={false}
          sx={{ mb: 2 }}
        />
      )}
      <Stack
        ref={scrollRef}
        direction="row"
        flexWrap="wrap"
        alignContent="flex-start"
        sx={{
          gap: 2,
          ...(LONG && {
            // DYNAMIC cap: the facet grows into the drawer's spare height and only scrolls
            // WITHIN itself once it runs out — instead of stopping at a fixed number while the
            // drawer sits part-empty. The old value was a flat `260`, which on a tall window
            // hid 146px of Best for's 406px of options behind the fade while 244px of the field
            // list below it was blank (measured, 1914x1304). That is the bug this fixes.
            //
            // `100vh - 800px` is measured, not guessed: the field list is `calc(100vh - 172px)`
            // and this drawer's other five facets consume ~630px of it, so ~`100vh - 800px` is
            // the height actually free for this facet. The `max(260px, …)` keeps the old 260 as
            // a FLOOR, so on a short window the cap lands right back on today's value and the
            // documented intent below still holds.
            //
            // Resolved cap by window height: 1304 -> 504 (all 406px of Best for visible, no
            // internal scroll, drawer still does not scroll); 800 -> 260 (byte-identical to the
            // previous behaviour). Both measured in the browser, not derived.
            //
            // Still a MAX, never a min — the reason the original comment gave for having no
            // `minHeight` is preserved exactly: a facet that trips the "long" threshold with
            // FEW options (e.g. Size, 13) renders at its natural height, never a padded box
            // leaving a gap before the next facet. And no `flexGrow`, deliberately: growing past
            // the content only adds invisible dead space (this block has no border or fill), and
            // on a drawer where a long facet is NOT last it would push every later facet to the
            // bottom. Growth is bounded by content, then by the cap.
            maxHeight: 'max(260px, calc(100vh - 800px))',
            overflowY: 'auto',
            overflowX: 'hidden',
            // NO `pr` inset. The long branch used to reserve 4px for the scrollbar, which made
            // this the ONE field in the drawer whose content stopped short of the right edge:
            // measured 448px of chip area under a 452px full-width search box. The scrollbar is
            // hidden instead (the same `hideScrollbar` the drawer's Paper and field list already
            // use, and the same scroll+fade+hidden-scrollbar pairing as `Activity`), so the
            // chips get the full 452 and the bottom fade below carries "there is more".
            // Cast because `hideScrollbar` is exported as `SxProps<Theme>`, which is a UNION
            // (object | array | function). Spreading a union into an object literal has no
            // matching overload. `Filters.tsx` spreads the same mixin without a cast only
            // because its `sx` is the FUNCTION form, where the return type is inferred loosely.
            // The mixin is a plain object; the over-broad export type is the real defect, and
            // narrowing it belongs in `@repo/ui`, not in a late fix on this branch.
            ...(hideScrollbar as Record<string, unknown>),
            // Fade the bottom edge while more rows are below; clears at the bottom.
            transition: 'mask-image .15s, -webkit-mask-image .15s',
            ...fadeSx,
          }),
        }}
      >
        {shown.map((option, index) => {
          const isSelected = (selectedValues as any[]).includes(option)
          const statusConfig = statuses?.[option]

          // Status pill treatment (matches StatusChipFilter): highlighted in the
          // status's own color when selected, grey/dim outline when not. Constant
          // 1px border + padding in both states so toggling never shifts layout.
          if (statusConfig) {
            const statusColor = statusConfig.color
            return (
              <Stack
                key={index}
                direction="row"
                alignItems="center"
                spacing={2}
                onClick={() => handleSelect(option)}
                sx={{
                  px: 3,
                  py: 2,
                  cursor: 'pointer',
                  borderRadius: '999px',
                  border: '1px solid',
                  borderColor: isSelected ? statusColor : theme.palette.grey[300],
                  backgroundColor: isSelected ? alpha(statusColor, 0.14) : 'transparent',
                  color: isSelected ? statusColor : theme.palette.text.secondary,
                  transition: 'background-color .12s, border-color .12s, color .12s',
                  '&:hover': {
                    borderColor: statusColor,
                    backgroundColor: alpha(statusColor, isSelected ? 0.18 : 0.08),
                  },
                }}>
                <Typography sx={{ fontSize: 12, fontWeight: 500, lineHeight: 1 }}>
                  {statusConfig.label}
                </Typography>
              </Stack>
            )
          }

          return (
            <Stack
              key={index}
              onClick={() => handleSelect(option)}
              sx={{
                // Height parity with the StatusChipFilter pills (px:4 / py:2.5 / 16px
                // icon): a multiSelect option that renders an icon cell (e.g. the Type
                // column's CategoryCell, whose lucide icon is 24px by default) otherwise
                // reads taller than the status pills sitting next to it in the drawer.
                px: 4,
                // Fixed height + centered content so every filter chip (text-only Size,
                // icon+label Best for, and the Capability badges) is exactly the same height.
                minHeight: 38,
                justifyContent: 'center',
                cursor: 'pointer',
                borderRadius: 2,
                border: `1px solid ${theme.palette.grey[300]}`,
                backgroundColor:  selectedValues.includes(option) && alpha(theme.palette.brand[500], .1),
                borderColor:  selectedValues.includes(option) && theme.palette.brand[500],
                // Keep label at the theme's primary text color (white in dark mode)
                // — the purple border + faint purple fill are the selection cue.
                // brand[900] dark-purple text was illegible on the dark chip.
                color: theme.palette.text.primary,
                // Constrain any rendered icon (CategoryCell etc.) to the pill's 16px and
                // tighten the label's line-height to 1 (the StatusChip pill's), so the
                // chip height matches the status pills exactly.
                '& svg': { width: 16, height: 16 },
                '& .MuiTypography-root': { lineHeight: 1 },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.brand[500], .1),
                  borderColor: theme.palette.brand[500],
                  color: theme.palette.text.primary
                }
              }}>
              {column.renderCell != undefined ?  (
                <>
                  {column.renderCell({
                    value: option,
                    filter: true,
                    row: {
                      [column.field]: option,
                    },
                  })}
                </>
              ) : (
                /*
                  Key-shaped values get `_`→space + a leading capital; anything that already
                  reads as display text is printed verbatim. See `formatEnumLabel` — this used
                  to be `capitalize(option.trim().replace('_', ' '))`, which had a defect at each
                  end: `capitalize` lower-cases everything after the first letter (so the company
                  facet rendered "Louis Vuitton" as "Louis vuitton"), and the non-global replace
                  only ever swapped the FIRST underscore (so `waiting_on_customer` would render
                  as "Waiting on_customer").
                */
                <>{typeof option == 'string' ? formatEnumLabel(option) : option}</>
               )}
            </Stack>
          )
        })}
      </Stack>
    </FilterField>
  )
}
